import {
	useCurrentUser,
	ITutotialStatus,
	useSetTutorialStatus,
	useTutorialStatus,
	useJurisdictions,
	useClientId,
} from 'api'
import { useLocalization } from 'localization'
import { useCallback, useEffect, useMemo } from 'react'
import { useScreenMatch } from 'theme/hooks'
import { useShowModal } from './GlobalModals'
import { ITutorialModal } from './TutorialModal'
import { useAppContext } from './appContext'
import appRoutes from './app-routes'
import { buildAppUrl } from 'utils/string'

export const useShowTutorial = () => {
	const { t } = useLocalization()
	const showModal = useShowModal()
	const { mutateAsync: setTutorialStatus } = useSetTutorialStatus()
	const { data: status } = useTutorialStatus()
	const clientId = useClientId()
	const isWealthicaUser = useIsWealthicaUser()

	return (tutorial: keyof ITutotialStatus) => {
		if (isWealthicaUser) {
			const link = document.createElement('a')

			switch (tutorial) {
				case 'imports':
					link.href = buildAppUrl('/tutorial/Wealthica wallets.pdf')
					link.download = 'Wealthica wallets.pdf'
					break
				case 'reports':
					link.href = buildAppUrl('/tutorial/Wealthica reports.pdf')
					link.download = 'Wealthica reports.pdf'
					break
				case 'transactions':
					link.href = buildAppUrl('/tutorial/Wealthica transactions.pdf')
					link.download = 'Wealthica transactions.pdf'
					break
				default:
			}
			link.dispatchEvent(new MouseEvent('click'))

			return
		}

		const onClose = async () => {
			if (!clientId) {
				await setTutorialStatus({ ...status, [tutorial]: true })
			}
		}
		let params: ITutorialModal

		switch (tutorial) {
			case 'imports':
				params = {
					title: t('settings.tutorials.wallets.title'),
					url: buildAppUrl('/tutorial/Importing data full.mp4'),
					instructions: t('settings.tutorials.wallets.instructions'),
					onClose,
				}
				break
			case 'transactions':
				params = {
					title: t('settings.tutorials.transactions.title'),
					url: buildAppUrl('/tutorial/Transactions.mp4'),
					instructions: t('settings.tutorials.transactions.instructions'),
					onClose,
				}
				break
			case 'reports':
			default:
				params = {
					title: t('settings.tutorials.reports.title'),
					url: buildAppUrl('/tutorial/Tax Reports.mp4'),
					instructions: t('settings.tutorials.reports.instructions'),
					onClose,
				}
		}

		showModal('tutorial', params)
	}
}

export const useUserCurrency = () => {
	const { data: user } = useCurrentUser()
	const { data: jurisdictions = [] } = useJurisdictions()

	return useMemo(() => {
		const foundJurisdiction = jurisdictions.find(({ shortName }) => user?.jurisdiction === shortName)
		return foundJurisdiction?.currency ?? ''
	}, [user, jurisdictions])
}

// to have up to date reference inside event handler
let closureMatch: ReturnType<typeof useScreenMatch>

export const useZendesk = () => {
	const match = useScreenMatch()
	closureMatch = match
	const { isWealthicaContext } = useAppContext()

	const adjustMediaQuery = useCallback(() => {
		if (isWealthicaContext) {
			zE('messenger:set', 'cookies', false)
			return
		}
		// show/hide zendesk widget on certain side
		zE('messenger:set', 'cookies', match.gte('md'))
	}, [match, isWealthicaContext])

	useEffect(() => {
		zE('messenger:on', 'close', function () {
			if (!closureMatch.gte('md')) {
				setTimeout(() => {
					zE('messenger:set', 'cookies', false)
				}, 100)
			}
		})
	}, [])

	useEffect(() => {
		adjustMediaQuery()

		window.addEventListener('resize', adjustMediaQuery)
		return () => {
			window.removeEventListener('resize', adjustMediaQuery)
		}
	}, [adjustMediaQuery])
}

export const useIsWealthicaUser = () => {
	const { isWealthicaContext } = useAppContext()
	const { data: user } = useCurrentUser()

	return useMemo(() => user?.externalProvider?.type === 'WEALTHICA' || isWealthicaContext, [user, isWealthicaContext])
}

export const useShowWealthicaPlanUpgradeRedirect = () => {
	const { t } = useLocalization()
	const showModal = useShowModal()

	return () => {
		showModal('wealthica.upgrade.redirect', {
			title: t('billings.wealthica.upgrade.title'),
			subtitle: t('billings.wealthica.upgrade.subtitle'),
			okText: t('actions.proceed'),
			submit: () => {
				window.open(appRoutes.external.wealthicaAddonDetails, '_blank')
			},
		})
	}
}
